// src/components/CustomersView.tsx
import React, { useState } from 'react';
import { Merge, Trash2, ClipboardCopy, AlertTriangle, X, Check, ChevronDown, ChevronUp } from 'lucide-react';

interface CustomersViewProps {
    customers: any[];
    
    // Klanten toevoegen
    newCustomer: any;
    setNewCustomer: (val: any) => void;
    addCustomer: (e: React.FormEvent) => void;
    
    // Bulk acties en selecties
    selectedCustomerIds: Set<string>;
    toggleCustomerSelection: (id: string) => void;
    handleBulkDeleteCustomers: () => void;
    initiateMerge: (ids?: string[]) => void;
    
    // Wordt aangeroepen als de gebruiker de samenvoeging definitief maakt in de modal
    confirmMerge?: (primaryId: string, secondaryIds: string[]) => void; 
    
    // Dubbele klanten
    duplicateGroups: any[][];
    
    // Acties op een individuele klant
    openCustomerHistory: (customer: any) => void;
    
    // Import
    setImportText: (val: string) => void;
    setShowCustomerImportModal: (val: boolean) => void;
}

export default function CustomersView({
    customers,
    newCustomer, setNewCustomer, addCustomer,
    selectedCustomerIds, toggleCustomerSelection, handleBulkDeleteCustomers, initiateMerge, confirmMerge,
    duplicateGroups,
    openCustomerHistory,
    setImportText, setShowCustomerImportModal
}: CustomersViewProps) {
    
    // Lokale state voor de samenvoeg-modal
    const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
    const [mergeCandidates, setMergeCandidates] = useState<any[]>([]);
    const [primaryMergeId, setPrimaryMergeId] = useState<string>('');
    const [expandedCandidateIds, setExpandedCandidateIds] = useState<Set<string>>(new Set());

    const handleMergeClick = (ids?: string[]) => {
        const targetIds = ids || Array.from(selectedCustomerIds);
        if (targetIds.length < 2) {
            alert('Selecteer minimaal 2 klanten om samen te voegen.');
            return;
        }

const candidates = customers.filter(c => targetIds.indexOf(c.id) !== -1);        if (candidates.length < 2) return;

        setMergeCandidates(candidates);
        setPrimaryMergeId(candidates[0].id);
        setExpandedCandidateIds(new Set()); // Reset uitklapvensters
        setIsMergeModalOpen(true);
        
        if (initiateMerge) initiateMerge(targetIds);
    };

    const handleConfirmMerge = () => {
        if (confirmMerge) {
            const secondaryIds = mergeCandidates.filter(c => c.id !== primaryMergeId).map(c => c.id);
            confirmMerge(primaryMergeId, secondaryIds);
        } else {
            console.warn("Zorg ervoor dat 'confirmMerge' als prop wordt doorgegeven vanuit de parent om de wijziging door te voeren.");
        }
        setIsMergeModalOpen(false);
        setMergeCandidates([]);
        setPrimaryMergeId('');
        setExpandedCandidateIds(new Set());
    };

    const toggleCandidateDetails = (e: React.MouseEvent, id: string) => {
        e.stopPropagation(); // Voorkom dat de rij wordt geselecteerd als hoofdprofiel
        const newExpanded = new Set(expandedCandidateIds);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedCandidateIds(newExpanded);
    };

    return (
        <div className="animate-in fade-in duration-500 relative">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-slate-800 dark:text-white">Klantenbestand <span className="text-slate-400 text-sm font-normal">({customers.length})</span></h2>
                <div className="flex gap-2">
                    {selectedCustomerIds.size > 0 && (
                        <>
                            <button 
                                onClick={() => handleMergeClick()}
                                className="flex items-center px-4 py-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-xl hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition mr-2"
                                title="Voeg geselecteerde klanten samen tot één"
                            >
                                <Merge className="w-4 h-4 mr-2" />
                                Samenvoegen ({selectedCustomerIds.size})
                            </button>
                            <button 
                                onClick={handleBulkDeleteCustomers}
                                className="flex items-center px-4 py-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/50 transition"
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Verwijder ({selectedCustomerIds.size})
                            </button>
                        </>
                    )}
                    <button onClick={() => { setImportText(''); setShowCustomerImportModal(true); }} className="flex items-center px-4 py-2 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-xl hover:bg-green-100 dark:hover:bg-green-900/50 border border-green-200 dark:border-green-800"><ClipboardCopy className="w-4 h-4 mr-2" />Plak uit Excel</button>
                </div>
            </div>
            
            {duplicateGroups.length > 0 && (
                <div className="mb-6 bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800 rounded-xl p-4">
                    <h3 className="font-bold text-orange-800 dark:text-orange-200 flex items-center mb-2">
                        <AlertTriangle className="w-4 h-4 mr-2" /> 
                        Mogelijke dubbele klanten gevonden ({duplicateGroups.length})
                    </h3>
                    <div className="space-y-2">
                        {duplicateGroups.map((group, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm bg-white dark:bg-slate-800 p-2 rounded-lg border border-orange-100 dark:border-orange-900/50">
                                <span className="text-slate-700 dark:text-slate-300">
                                    <strong>{group[0].name}</strong> komt {group.length} keer voor.
                                </span>
                                <button 
                                   onClick={() => handleMergeClick(group.map((c: any) => c.id))}
                                   className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium flex items-center"
                                >
                                    Samenvoegen
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* SAMENVOEGEN MODAL */}
            {isMergeModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-xl w-full max-w-3xl border border-slate-200 dark:border-slate-700 max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold text-slate-800 dark:text-white flex items-center">
                                <Merge className="w-5 h-5 mr-2 text-indigo-600 dark:text-indigo-400" />
                                Klanten Samenvoegen
                            </h3>
                            <button onClick={() => setIsMergeModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                            Selecteer het <strong>hoofdprofiel</strong> dat je wilt behouden. Klap de profielen uit om de achterliggende bespanningen en data te vergelijken. De andere profielen worden verwijderd.
                        </p>
                        
                        <div className="space-y-3 mb-6 overflow-y-auto pr-2 flex-1">
                            {mergeCandidates.map(candidate => {
                                const isExpanded = expandedCandidateIds.has(candidate.id);
                                return (
                                    <div 
                                        key={candidate.id} 
                                        className={`rounded-xl border-2 transition ${primaryMergeId === candidate.id ? 'border-indigo-600 bg-indigo-50/30 dark:bg-indigo-900/10' : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-700'}`}
                                    >
                                        {/* Hoofdrij (Klikbaar om te selecteren) */}
                                        <div 
                                            onClick={() => setPrimaryMergeId(candidate.id)}
                                            className="p-4 flex items-center justify-between cursor-pointer"
                                        >
                                            <div className="flex-1">
                                                <div className="font-bold text-slate-800 dark:text-white flex items-center">
                                                    {candidate.name}
                                                    {primaryMergeId === candidate.id && (
                                                        <span className="ml-3 text-[10px] uppercase tracking-wider bg-indigo-600 text-white px-2 py-0.5 rounded-full font-bold">
                                                            Hoofdprofiel
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-sm text-slate-500 dark:text-slate-400 mt-1 flex gap-4">
                                                    {candidate.club && <span><strong>Club:</strong> {candidate.club}</span>}
                                                    {candidate.phone && <span><strong>Tel:</strong> {candidate.phone}</span>}
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-4 ml-4">
                                                <button 
                                                    onClick={(e) => toggleCandidateDetails(e, candidate.id)}
                                                    className="flex items-center text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 px-3 py-1.5 rounded-lg transition"
                                                >
                                                    {isExpanded ? 'Verberg data' : 'Bekijk alle data'}
                                                    {isExpanded ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
                                                </button>
                                                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${primaryMergeId === candidate.id ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-300 dark:border-slate-600'}`}>
                                                    {primaryMergeId === candidate.id && <Check className="w-4 h-4" />}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Uitgeklapte details sectie */}
                                        {isExpanded && (
                                            <div className="px-4 pb-4 pt-2 border-t border-slate-200 dark:border-slate-700 text-sm animate-in slide-in-from-top-2">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                    <div className="bg-white dark:bg-slate-800 p-3 rounded-lg border border-slate-100 dark:border-slate-700 shadow-sm">
                                                        <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-2 border-b border-slate-100 dark:border-slate-700 pb-1">Klantgegevens</h4>
                                                        <p className="text-slate-600 dark:text-slate-400"><strong className="text-slate-800 dark:text-slate-200">Email:</strong> {candidate.email || '-'}</p>
                                                        <p className="text-slate-600 dark:text-slate-400"><strong className="text-slate-800 dark:text-slate-200">Telefoon:</strong> {candidate.phone || '-'}</p>
                                                        <p className="text-slate-600 dark:text-slate-400"><strong className="text-slate-800 dark:text-slate-200">Club:</strong> {candidate.club || '-'}</p>
                                                    </div>
                                                    <div className="bg-white dark:bg-slate-800 p-3 rounded-lg border border-slate-100 dark:border-slate-700 shadow-sm">
                                                        <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-2 border-b border-slate-100 dark:border-slate-700 pb-1">Standaard Voorkeuren</h4>
                                                        <p className="text-slate-600 dark:text-slate-400"><strong className="text-slate-800 dark:text-slate-200">Merk:</strong> {candidate.defaultRacketBrand || '-'}</p>
                                                        <p className="text-slate-600 dark:text-slate-400"><strong className="text-slate-800 dark:text-slate-200">Model:</strong> {candidate.defaultRacketModel || '-'}</p>
                                                        <p className="text-slate-600 dark:text-slate-400"><strong className="text-slate-800 dark:text-slate-200">Snaar:</strong> {candidate.defaultString || '-'}</p>
                                                        <p className="text-slate-600 dark:text-slate-400"><strong className="text-slate-800 dark:text-slate-200">Kilo's:</strong> {candidate.defaultTension || '-'}</p>
                                                    </div>
                                                </div>

                                                <div className="bg-slate-50 dark:bg-slate-900/50 p-3 rounded-lg border border-slate-100 dark:border-slate-700">
                                                    <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-2">Historie & Bespanningen ({candidate.history?.length || 0})</h4>
                                                    {candidate.history && candidate.history.length > 0 ? (
                                                        <ul className="space-y-2 max-h-40 overflow-y-auto pr-2">
                                                            {candidate.history.map((job: any, i: number) => (
                                                                <li key={i} className="text-xs bg-white dark:bg-slate-800 p-2 rounded border border-slate-200 dark:border-slate-700 flex justify-between items-center">
                                                                    <div>
                                                                        <span className="font-semibold text-slate-800 dark:text-slate-200">{job.racket || 'Onbekend racket'}</span>
                                                                        <span className="text-slate-500 block mt-0.5">{job.string || 'Onbekende snaar'} @ {job.tension || '?'}kg</span>
                                                                    </div>
                                                                    <div className="text-slate-400">{job.date || 'Geen datum'}</div>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <p className="text-xs text-slate-500 italic">Geen bespanningen of historie gevonden voor dit profiel.</p>
                                                    )}
                                                </div>

                                                {candidate.notes && (
                                                    <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-900/50 rounded-lg">
                                                        <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-1">Notities</h4>
                                                        <p className="text-xs text-yellow-700 dark:text-yellow-300 whitespace-pre-wrap">{candidate.notes}</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-700 mt-auto">
                            <button 
                                onClick={() => setIsMergeModalOpen(false)}
                                className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition font-medium"
                            >
                                Annuleren
                            </button>
                            <button 
                                onClick={handleConfirmMerge}
                                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition flex items-center shadow-sm"
                            >
                                Definitief Samenvoegen
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                   <ul className="divide-y divide-slate-100 dark:divide-slate-700">
                     {customers.length === 0 ? <li className="p-8 text-center text-slate-400">Nog geen klanten.</li> : customers.map(c => (
                       <li key={c.id} className={`p-4 flex items-center hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer group ${selectedCustomerIds.has(c.id) ? 'bg-indigo-50/50 dark:bg-indigo-900/30' : ''}`}>
                         <div className="mr-4" onClick={(e) => e.stopPropagation()}>
                            <input 
                                type="checkbox" 
                                checked={selectedCustomerIds.has(c.id)} 
                                onChange={() => toggleCustomerSelection(c.id)}
                                className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500 border-gray-300 dark:border-slate-600 dark:bg-slate-700 cursor-pointer"
                            />
                         </div>
                         <div className="flex-1 flex justify-between items-center" onClick={() => openCustomerHistory(c)}>
                             <div className="flex items-center">
                                 <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-300 flex items-center justify-center font-bold mr-4">{c.name.charAt(0)}</div>
                                 <div><p className="font-medium text-slate-800 dark:text-white">{c.name}</p><p className="text-xs text-slate-500 dark:text-slate-400">{c.club}</p></div>
                             </div>
                             <div className="text-sm text-slate-500 dark:text-slate-400">{c.phone}</div>
                         </div>
                       </li>
                     ))}
                   </ul>
                </div>
                <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 h-fit">
                    <h3 className="font-bold text-slate-800 dark:text-white mb-4">Nieuwe Klant</h3>
                    <form onSubmit={addCustomer} className="space-y-4">
                        <input type="text" placeholder="Naam" className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border-none text-slate-900 dark:text-white selection:bg-lime-300 selection:text-slate-900" value={newCustomer.name} onChange={(e) => setNewCustomer({...newCustomer, name: e.target.value})} required />
                        <input type="text" placeholder="Vereniging / Club" className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border-none text-slate-900 dark:text-white selection:bg-lime-300 selection:text-slate-900" value={newCustomer.club} onChange={(e) => setNewCustomer({...newCustomer, club: e.target.value})} />
                        <input type="text" placeholder="Telefoonnummer" className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border-none text-slate-900 dark:text-white selection:bg-lime-300 selection:text-slate-900" value={newCustomer.phone} onChange={(e) => setNewCustomer({...newCustomer, phone: e.target.value})} />
                        <div className="pt-4 mt-2 border-t border-slate-100 dark:border-slate-700">
                            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-2 uppercase">Voorkeuren</p>
                            <input type="text" placeholder="Merk Racket" className="w-full p-3 bg-slate-50 dark:bg-slate-900 rounded-xl mb-2 text-slate-900 dark:text-white selection:bg-lime-300 selection:text-slate-900" value={newCustomer.defaultRacketBrand} onChange={e => setNewCustomer({...newCustomer, defaultRacketBrand: e.target.value})} />
                            <input type="text" placeholder="Model" className="w-full p-3 bg-slate-50 dark:bg-slate-900 rounded-xl mb-2 text-slate-900 dark:text-white selection:bg-lime-300 selection:text-slate-900" value={newCustomer.defaultRacketModel} onChange={e => setNewCustomer({...newCustomer, defaultRacketModel: e.target.value})} />
                            <input type="text" placeholder="Snaar" className="w-full p-3 bg-slate-50 dark:bg-slate-900 rounded-xl text-slate-900 dark:text-white selection:bg-lime-300 selection:text-slate-900" value={newCustomer.defaultString} onChange={e => setNewCustomer({...newCustomer, defaultString: e.target.value})} />
                        </div>
                        <button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-xl font-bold transition">Toevoegen</button>
                    </form>
                </div>
            </div>
        </div>
    );
}